/* eslint-disable jsx-a11y/iframe-has-title */
import { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { ref, onValue, off, set } from "firebase/database";
import { database } from "../constants/firebase";
import { ICONS } from "../constants/icons";
import useMoveTo from "../constants/navigation";
import { IMAGES } from "../constants/images";
import Sidebar from "../components/Sidebar";
import { useUser } from "../contexts/UserContext";
import MobileHeader from "../components/MobileHeader";
import BunnyVideoEmbed from "../components/BunnyVideoEmbed";
import { LuLink } from "react-icons/lu";

const ViewLesson = () => {
    const navigate = useMoveTo();
    const { productId } = useParams();
    const { currentUser } = useUser();

    const location = useLocation();
    const lessonId = location.state?.lessonId;
    const chapterId = location.state?.chapterId;

    const [lesson, setLesson] = useState(null);
    const [creator, setCreator] = useState("Unknown");
    const [creatorData, setCreatorData] = useState(null);
    const [selectedLesson, setSelectedLesson] = useState(null);
    const [isIframeLoading, setIsIframeLoading] = useState(true);
    const [course, setCourse] = useState(null);
    const [hasPurchased, setHasPurchased] = useState(false);
    const [checkingAccess, setCheckingAccess] = useState(true);

    // ✅ Check if user has purchased the course
    useEffect(() => {
        if (!currentUser?.uid || !productId) return;

        const courseRef = ref(database, `Listings/${productId}`);
        const libraryRef = ref(database, `Users/${currentUser.uid}/library/${productId}`);

        // Load course info
        const unsubscribeCourse = onValue(courseRef, (snapshot) => {
            if (snapshot.exists()) {
                setCourse(snapshot.val());
            }
        });

        // Check library entry
        const unsubscribeLibrary = onValue(libraryRef, (snapshot) => {
            const exists = snapshot.exists();
            if (exists) {
                const data = snapshot.val();
                setHasPurchased(!!data.purchasePrice || !!data.totalPaid);
            } else {
                setHasPurchased(false);
            }
            setCheckingAccess(false);
        });

        return () => {
            unsubscribeCourse();
            unsubscribeLibrary();
        };
    }, [currentUser?.uid, productId]);

    // ✅ Check access and redirect if needed
    useEffect(() => {
        if (checkingAccess || !course) return;

        const coursePrice = parseFloat(course.price || 0);
        const isFree = coursePrice === 0;
        const isOwner = course.userId === currentUser?.uid;

        // If course is paid and user hasn't purchased it and isn't the owner, redirect
        if (!isFree && !hasPurchased && !isOwner) {
            window.showToast("Please purchase this course to access its content.", "info");
            navigate(`/product-details/${productId}`);
        }
    }, [checkingAccess, course, hasPurchased, currentUser?.uid, productId, navigate]);

    // ✅ Load lesson details
    useEffect(() => {
        const lessonRef = ref(database, `Listings/${productId}/chapters/${chapterId}/lessons/${lessonId}`);
        const unsubscribe = onValue(lessonRef, (snapshot) => {
            if (snapshot.exists()) {
                setLesson({ id: lessonId, ...snapshot.val() });
            }
        });
        return () => off(lessonRef, "value", unsubscribe);
    }, [chapterId, lessonId, productId]);

    // ✅ Load creator info
    useEffect(() => {
        const productRef = ref(database, `Listings/${productId}`);
        const unsubscribe = onValue(productRef, (snapshot) => {
            if (snapshot.exists()) {
                const product = snapshot.val();
                setCourse(product); // Update course info
                if (product?.userId) {
                    const userRef = ref(database, `Users/${product.userId}`);
                    const unsubscribeUser = onValue(userRef, (snap) => {
                        if (snap.exists()) {
                            setCreator(snap.val().name || "Unknown");
                            setCreatorData(snap.val());
                        }
                    });
                    return () => off(userRef, "value", unsubscribeUser);
                }
            }
        });
        return () => off(productRef, "value", unsubscribe);
    }, [productId]);

    if (!lesson) {
        return (
            <div className="flex items-center justify-center h-screen text-gray-500">
                Loading lesson details...
            </div>
        );
    }

    return (
        <div className="h-screen flex flex-col bg-white text-black dark:bg-[#1A1A1A] dark:text-white overflow-y-auto">
            {/* Mobile Header */}
            <MobileHeader title={lesson.title || "Lesson Details"} isBackBtn={true} />

            <div className="flex flex-1 overflow-hidden">
                {/* Sidebar */}
                <Sidebar />

                {/* Scrollable Content */}
                <main className="flex-1 flex flex-col">
                    {/* Header */}
                    <div className="flex-shrink-0 pt-10 px-6 md:pb-3 pb-0">
                        <div className="flex items-center justify-between">
                            <button onClick={() => navigate(-1)} className="flex-shrink-0 md:block hidden">
                                <img
                                    src={ICONS.arrowBack}
                                    className="w-4 h-4 md:w-5 md:h-5 grayscale brightness-0 dark:invert"
                                    alt="Back"
                                />
                            </button>
                            <h2 className="text-2xl md:text-3xl font-semibold hidden md:block">
                                {lesson.title}
                            </h2>
                            <div className="w-5" />
                        </div>
                    </div>

                    {/* Middle Section */}
                    <div className="flex-1 overflow-y-auto px-6 md:pt-6 pt-0 pb-16 md:pb-6">
                        <div className="flex flex-col md:flex-row gap-6">
                            {/* Lesson Image */}
                            <div className="md:w-1/2 lg:w-[40%]">
                                <img
                                    src={lesson.coverImageUrl || IMAGES.courses}
                                    alt="Lesson"
                                    className="rounded-xl w-full h-96 md:h-[600px] object-cover"
                                />
                            </div>

                            {/* Lesson Content */}
                            <div className="flex-1">
                                <h1 className="text-3xl font-bold">{lesson.title}</h1>
                                <p className="text-gray-500 dark:text-white/50 mt-1 flex items-center gap-2
                                                               "
                                >Created by: {''}
                                    <span
                                        className={`flex items-center gap-1 font-semibold cursor-pointer ${creatorData?.role === "Host"
                                            ? "dark:glow-text-dark glow-text-light"
                                            : "text-gray-500 dark:text-white/50"
                                            }`}
                                    >
                                        {creator}
                                        {creatorData?.isTeamUser && (
                                            <svg width="16px" height="16px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="#327691">
                                                <path d="M10.5213 2.62368C11.3147 1.75255 12.6853 1.75255 13.4787 2.62368L14.4989 3.74391C14.8998 4.18418 15.4761 4.42288 16.071 4.39508L17.5845 4.32435C18.7614 4.26934 19.7307 5.23857 19.6757 6.41554L19.6049 7.92905C19.5771 8.52388 19.8158 9.10016 20.2561 9.50111L21.3763 10.5213C22.2475 11.3147 22.2475 12.6853 21.3763 13.4787L20.2561 14.4989C19.8158 14.8998 19.5771 15.4761 19.6049 16.071L19.6757 17.5845C19.7307 18.7614 18.7614 19.7307 17.5845 19.6757L16.071 19.6049C15.4761 19.5771 14.8998 19.8158 14.4989 20.2561L13.4787 21.3763C12.6853 22.2475 11.3147 22.2475 10.5213 21.3763L9.50111 20.2561C9.10016 19.8158 8.52388 19.5771 7.92905 19.6049L6.41553 19.6757C5.23857 19.7307 4.26934 18.7614 4.32435 17.5845L4.39508 16.071C4.42288 15.4761 4.18418 14.8998 3.74391 14.4989L2.62368 13.4787C1.75255 12.6853 1.75255 11.3147 2.62368 10.5213L3.74391 9.50111C4.18418 9.10016 4.42288 8.52388 4.39508 7.92905L4.32435 6.41553C4.26934 5.23857 5.23857 4.26934 6.41554 4.32435L7.92905 4.39508C8.52388 4.42288 9.10016 4.18418 9.50111 3.74391L10.5213 2.62368Z" stroke="#327691" strokeWidth="1.5"></path>
                                                <path d="M9 12L11 14L15 10" stroke="#327691" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
                                            </svg>
                                        )}
                                    </span>
                                </p>

                                <h3 className="mt-3 text-xl font-semibold border-t border-gray-300 dark:border-[#1D1F24] pt-2">
                                    Description
                                </h3>
                                <p className="mt-3 dark:text-gray-300 text-gray-500 leading-relaxed whitespace-pre-line">
                                    {lesson.description || "No description available."}
                                </p>

                                {/* ✅ Lesson Links */}
                                {lesson.courseLinks?.length > 0 && (
                                    <div className="mt-4">
                                        <h4 className="font-semibold text-base mb-2 flex items-center gap-2">
                                            <LuLink className="text-[#327691]" />
                                            Lesson Links
                                        </h4>

                                        <ul className="space-y-2">
                                            {lesson.courseLinks.map((link, index) => (
                                                <li key={index}>
                                                    <a
                                                        href={link}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-[#327691] hover:underline break-all"
                                                    >
                                                        {link}
                                                    </a>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* ✅ Bottom Section (Play Video Button) */}
                    <div className="flex-shrink-0">
                        <div className="w-full dark:bg-[#1A1A1A] bg-white dark:text-white border-t border-gray-300 dark:border-[#1D1F24]">
                            <div className="max-w-3xl mx-auto flex flex-col md:flex-row items-center justify-between px-4 md:px-10 py-4 gap-8 md:pb-4 pb-24">
                                {/* Play Video */}
                                {lesson.fileUrl && (
                                    <button
                                        onClick={() => {
                                            setIsIframeLoading(true);
                                            setSelectedLesson(lesson);
                                        }}
                                        className="w-full h-full flex-1 md:w-auto px-6 py-3 rounded-xl font-semibold shadow-md transition bg-[#327691] hover:opacity-90 text-white"
                                    >
                                        Play Video
                                    </button>
                                )}

                            </div>
                        </div>
                    </div>
                </main>
            </div>

            {/* ✅ Video Modal */}
            {selectedLesson && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-[#1A1A1A] rounded-xl shadow-xl p-6 w-[90%] md:w-[70%] lg:w-[70%] xl:w-[70%] relative">
                        {/* Close Button */}
                        <button
                            className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white"
                            onClick={() => setSelectedLesson(null)}
                        >
                            ✖
                        </button>

                        <h3 className="text-xl font-semibold mb-4">{selectedLesson.title}</h3>

                        {/* ✅ Detect File Type */}
                        {selectedLesson.fileUrl && selectedLesson.fileUrl.endsWith(".pdf") ? (
                            // --- PDF Viewer ---
                            <iframe
                                src={selectedLesson.fileUrl}
                                title="PDF Viewer"
                                className="w-full h-[450px] rounded-lg border-0"
                            ></iframe>
                        ) : selectedLesson.fileUrl?.includes("iframe.mediadelivery.net") && (
                            // --- Bunny.net Video Embed ---
                            <BunnyVideoEmbed
                                videoUrl={selectedLesson.fileUrl}
                                orientation={selectedLesson.videoOrientation}
                                videoWidth={selectedLesson.videoWidth}
                                videoHeight={selectedLesson.videoHeight}
                                variant="modal"
                                isLoading={isIframeLoading}
                                onLoad={() => setIsIframeLoading(false)}
                                wrapperClassName="mx-auto shadow-xl"
                                loadingIndicator={null}
                                title={selectedLesson.title || "Lesson video"}
                            />
                        )}

                        {/* <p className="mt-4 text-gray-600 dark:text-gray-300">
                            {selectedLesson.description || "No description provided."}
                        </p> */}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ViewLesson;
